import ChipInput from 'components/ChipInput';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import * as React from 'react';
import _ from 'lodash';

interface Props {
  onSearch: (values: string[]) => void;
  setContainerNumbers?: (containerNumbers: string[]) => void;
  inputValues: string[];
  maximum: number;
  checkIsValid?: (value: string) => boolean;
  placeholder: string;
  className?: string;
  variant: 'magnifying-glass' | 'rounded' | 'simple';
  includeInvalid?: boolean;
}

/**
 * Renders an input for searching through the available containers
 * @returns
 */
export const GenericSearch: React.FC<Props> = (props) => {
  const chipInputRef = React.useRef<ChipInput>(null);
  const {
    onSearch,
    setContainerNumbers,
    inputValues,
    maximum,
    checkIsValid,
    placeholder,
    className,
    variant,
    includeInvalid,
  } = props;
  const [isMaximumReached, setIsMaximumReached] = React.useState<boolean>(false);
  const [chips, setChips] = React.useState<string[]>(inputValues || []);

  const handleOnChange = (values: string[], validatedValues: string[]) => {
    setChips(values);
    if (setContainerNumbers) setContainerNumbers(values);
    if (!_.isEqual(inputValues, validatedValues)) {
      onSearch(validatedValues);
    }
  };

  const clearInput = () => {
    chipInputRef.current?.updateChips([]);
    setIsMaximumReached(false);
  };

  React.useEffect(() => {
    chipInputRef.current?.updateChips(inputValues);
  }, [inputValues]);

  const clear = chips.length > 0 && (
    <div className="flex items-center space-x-1.5 mr-4 cursor-pointer" onClick={clearInput}>
      <CloseIcon style={{ fontSize: 16 }} className="text-gray-600" />
      <h5 className="underline whitespace-nowrap	text-gray-600">
        Clear {variant === 'rounded' ? `(${chips.length})` : ''}
      </h5>
    </div>
  );

  const chipInput = (
    <ChipInput
      ref={chipInputRef}
      defaultValue={inputValues}
      classes={{
        inputContainer: 'flex-grow flex items-center overflow-x-auto',
        inputRoot: 'min-w-36 px-0',
        input: 'p-0 focus:!filter-none',
        copyButtonContainer: 'flex items-center',
        copyButtonIcon: 'text-gray-600',
        chip: {
          root: '',
          label: 'select-text',
        },
      }}
      InputProps={{
        'aria-label': 'search containers',
      }}
      placeholder={placeholder}
      transformChipLabel={(label: string) => label.toUpperCase()}
      transformChipValue={(value: string) => value.replace(/-/gi, '').toUpperCase()}
      removeCharacters={['"', "'", ' ', '“', '”']}
      checkIfChipIsValid={checkIsValid || (() => true)}
      onChange={handleOnChange}
      onDelete={() => isMaximumReached && setIsMaximumReached(false)}
      maxNumberChips={maximum}
      onMaximumReached={() => setIsMaximumReached(true)}
      disableCopy={variant !== 'rounded'}
      includeInvalidChips={includeInvalid}
    />
  );

  switch (variant) {
    case 'rounded':
      return (
        <div
          className={`flex flex-col rounded-full border border-gray-200 py-0 px-4 w-full h-8 ${className}`}
        >
          <div className="flex items-center w-full h-full self-start">
            {!chips.length && <SearchIcon fontSize="small" className="text-gray-600 mr-2.5" />}
            {clear}
            <div className="w-full h-full flex relative overflow-x-hidden overflow-y-hidden">
              {chipInput}
            </div>
          </div>
          {isMaximumReached && (
            <h4 className="text-red-900 mt-0.5">
              You can&apos;t search more than {maximum} values at once.
            </h4>
          )}
        </div>
      );
    case 'magnifying-glass':
      return (
        <div className="flex items-center space-x-2">
          <div className={`flex border-b pb-2 ${className}`}>
            <SearchIcon fontSize="small" className="text-gray-600 mr-2.5" />
            {chipInput}
          </div>
          {clear}
        </div>
      );
    case 'simple':
    default:
      return <div className={`border px-2 py-1 w-full ${className}`}>{chipInput}</div>;
  }
};
GenericSearch.displayName = 'GenericSearch';
